// Matches either a markdown link - [label](url) - or a bare http(s) url. Only
// http and https are accepted, so a message can never turn into a `javascript:`
// anchor. Trailing sentence punctuation is left out of a bare url so a link at
// the end of a sentence doesn't swallow the period that follows it.
const LINK_PATTERN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>"]*[^\s<>".,;:!?)\]]/g;

/**
 * Splits text into plain and url parts, in order, so a caller can render the
 * url parts as links while leaving everything else as text. Each part carries
 * the offset it starts at, which gives a renderer a stable react key.
 *
 * A url part carries both the `value` to point the link at and the `label` to
 * show for it. These differ for a markdown link, which is how a message asks
 * for wording like "Click here to purchase ..." instead of a raw url. For a
 * bare url the label is the url itself.
 *
 * Used for API error messages that carry a url the athlete needs to follow -
 * see getMissingMembershipErrorMessage in wcra-platform-api.
 */
export const splitTextOnUrls = (text = '') => {
  if (!text) {
    return [];
  }

  const parts = [];
  let lastIndex = 0;

  // `matchAll` isn't used here to stay consistent with the rest of the helpers,
  // which target the same browsers as the app's babel config.
  let match = LINK_PATTERN.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        value: text.slice(lastIndex, match.index),
        start: lastIndex,
      });
    }

    // label and href are only captured by the markdown branch of the pattern.
    // A bare url matches neither, and stands in as its own label.
    const [matched, label, href] = match;
    parts.push({
      type: 'url',
      value: href || matched,
      label: label || matched,
      start: match.index,
    });

    lastIndex = match.index + matched.length;
    match = LINK_PATTERN.exec(text);
  }

  // Reset so the shared regex doesn't carry lastIndex into the next call.
  LINK_PATTERN.lastIndex = 0;

  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      value: text.slice(lastIndex),
      start: lastIndex,
    });
  }

  return parts;
};
